// src/util/network/Requisitions.ts
//
// Network layer for the internal Requisition feature (EE-207).
// Endpoints mirror backend/apis/v1/ecommerce/Requisitions.py, mounted
// under /v1/ecommerce/requisitions.
//
// Request bodies are camelCase (backend input models use camelCase aliases);
// response bodies for the requisition/item/audit/list endpoints are
// snake_case (see the casing note in types/RequisitionTypes.ts).
import {
  CreateRequisitionType,
  EditableRequisitionFieldsType,
  RequisitionListFiltersType,
  RequisitionItemPrefillType,
  RequisitionListType,
  RequisitionOptionsType,
  RequisitionPartStatusType,
  RequisitionType,
  RequisitionWorkflowActionType,
  UpdateRequisitionType,
} from "types/RequisitionTypes";
import { FetchError } from "exceptions/exceptions";
import { API_URL } from "./common";

const ENDPOINT = `${API_URL}/v1/ecommerce/requisitions`;

const jsonHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const handleJsonResponse = async (res: Response) => {
  if (res.ok) return res.json();
  else throw new FetchError(await res.text(), res.status);
};

// ---------------------------------------------------------------------------
// Options / config
// ---------------------------------------------------------------------------

export const apiGetRequisitionOptions = (
  token: string
): Promise<RequisitionOptionsType> => {
  return fetch(`${ENDPOINT}/options`, {
    method: "GET",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export const apiCreateRequisition = (
  token: string,
  requisition: CreateRequisitionType
): Promise<RequisitionType> => {
  return fetch(ENDPOINT, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(requisition),
  }).then(handleJsonResponse);
};

export const apiUpdateRequisition = (
  token: string,
  id: number,
  requisition: UpdateRequisitionType
): Promise<RequisitionType> => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(requisition),
  }).then(handleJsonResponse);
};

// Draft-only (409 if the requisition has left Draft).
export const apiDeleteDraftRequisition = (
  token: string,
  id: number
): Promise<boolean> => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "DELETE",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

export const apiGetRequisition = (
  token: string,
  id: number
): Promise<RequisitionType> => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "GET",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// ---------------------------------------------------------------------------
// Dashboard list (#12) - scope + filters + server pagination/sort
// ---------------------------------------------------------------------------

export const apiGetRequisitions = (
  token: string,
  filters: RequisitionListFiltersType = {}
): Promise<RequisitionListType> => {
  const {
    scope = "mine",
    status,
    typeCode,
    purposeCode,
    requesterId,
    salesOrderId,
    orderItemId,
    quoteId,
    materialTypeCode,
    toolCategoryCode,
    supplierId,
    createdFrom,
    createdTo,
    search,
    page = 1,
    // The backend caps pageSize at 300; that ceiling is not a sane default -
    // a caller that omits it wants a page, not the whole table.
    pageSize = 25,
    sortKey = "id",
    sortDirection = "desc",
  } = filters;

  const params = new URLSearchParams();
  params.set("scope", scope);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  params.set("sortKey", sortKey);
  params.set("sortDirection", sortDirection);
  // Backend expects a single space-separated string of status values.
  if (status && status.length > 0) params.set("status", status.join(" "));
  if (typeCode) params.set("typeCode", typeCode);
  if (purposeCode) params.set("purposeCode", purposeCode);
  if (requesterId !== undefined) params.set("requesterId", String(requesterId));
  if (salesOrderId !== undefined) params.set("salesOrderId", String(salesOrderId));
  if (orderItemId !== undefined) params.set("orderItemId", String(orderItemId));
  if (quoteId !== undefined) params.set("quoteId", String(quoteId));
  if (materialTypeCode) params.set("materialTypeCode", materialTypeCode);
  if (toolCategoryCode) params.set("toolCategoryCode", toolCategoryCode);
  if (supplierId !== undefined) params.set("supplierId", String(supplierId));
  if (createdFrom) params.set("createdFrom", createdFrom);
  if (createdTo) params.set("createdTo", createdTo);
  if (search) params.set("search", search);

  return fetch(`${ENDPOINT}?${params.toString()}`, {
    method: "GET",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// ---------------------------------------------------------------------------
// Workflow actions
// ---------------------------------------------------------------------------

export const apiSubmitRequisition = (
  token: string,
  id: number
): Promise<RequisitionType> => {
  return fetch(`${ENDPOINT}/${id}/submit`, {
    method: "POST",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// There is no approve call here any more (EE-253). Approval is a transition on
// the linked Inquiry - apiUpdateInquiry with state=APPROVED - which syncs the
// requisition to Approved; the backend dropped /requisitions/<id>/approve with it.

// `comment` is mandatory for reject (enforced server-side, 400 if missing).
export const apiRejectRequisition = (
  token: string,
  id: number,
  comment: string
): Promise<RequisitionType> => {
  const action: RequisitionWorkflowActionType = { comment };
  return fetch(`${ENDPOINT}/${id}/reject`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(action),
  }).then(handleJsonResponse);
};

// Ready to Use -> Closed. The requester acknowledging the request is done;
// the backend closes the linked Inquiry with it. 409 from any other status.
export const apiCloseRequisition = (
  token: string,
  id: number,
  comment?: string
): Promise<RequisitionType> => {
  const action: RequisitionWorkflowActionType = { comment };
  return fetch(`${ENDPOINT}/${id}/close`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(action),
  }).then(handleJsonResponse);
};

// How many requisitions have a status update nobody has opened yet - the
// number on the Requisitions menu badge. Same flag as the row indicator, so
// the two agree; scope "all" by default because the flag is global.
export const apiCountRequisitionStatusUpdates = (
  token: string,
  scope: "mine" | "all" = "all"
): Promise<{ count: number }> => {
  return fetch(`${ENDPOINT}/status-updates/count?scope=${scope}`, {
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// Clears the unacknowledged-status indicator (EE-253). Not a workflow
// transition and not a status change - opening the requisition *is* the
// acknowledgement, so the detail dialog fires this on open. Idempotent: a
// requisition with nothing pending is a 200 that writes nothing.
export const apiAcknowledgeRequisitionStatus = (
  token: string,
  id: number
): Promise<RequisitionType> => {
  return fetch(`${ENDPOINT}/${id}/acknowledge-status`, {
    method: "POST",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// `reasonCode` is mandatory (400 if missing/unknown); `comment` is mandatory
// for reasons where the seeded row has requiresComment=true.
export const apiCancelRequisition = (
  token: string,
  id: number,
  reasonCode: string,
  comment?: string
): Promise<RequisitionType> => {
  const action: RequisitionWorkflowActionType = { reasonCode, comment };
  return fetch(`${ENDPOINT}/${id}/cancel`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(action),
  }).then(handleJsonResponse);
};

// One line of a submitted requisition, cancelled on its own: it leaves the
// pool or an unsent Inquiry, or is flagged source-cancelled on a sent one.
// Same reasons as a whole-requisition cancel. 409 on a Draft, Done or Closed
// requisition, and on its last open line (cancel the requisition instead).
export const apiCancelRequisitionLine = (
  token: string,
  id: number,
  lineId: number,
  reasonCode: string,
  comment?: string
): Promise<RequisitionType> => {
  const action: RequisitionWorkflowActionType = { reasonCode, comment };
  return fetch(`${ENDPOINT}/${id}/lines/${lineId}/cancel`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(action),
  }).then(handleJsonResponse);
};

// Edit-after-submit (#9): only estimatedDeliveryDate/supplierId/comments.
// 409 if the requisition is still a Draft (use apiUpdateRequisition)
// or already Closed.
export const apiUpdateRequisitionEditableFields = (
  token: string,
  id: number,
  changes: EditableRequisitionFieldsType
): Promise<RequisitionType> => {
  return fetch(`${ENDPOINT}/${id}/editable-fields`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(changes),
  }).then(handleJsonResponse);
};

// ---------------------------------------------------------------------------
// Part-level "active requisition" indicator (#14)
// ---------------------------------------------------------------------------

export const apiGetRequisitionPartStatus = (
  token: string,
  orderItemId: number
): Promise<RequisitionPartStatusType> => {
  const params = new URLSearchParams({ orderItemId: String(orderItemId) });
  return fetch(`${ENDPOINT}/part-status?${params.toString()}`, {
    method: "GET",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};

// ---------------------------------------------------------------------------
// Item prefill from the Sales Order or quote part's product (#12) -
// snake_case response like the other requisition GETs.
// ---------------------------------------------------------------------------

// Exactly one of the two: a requisition is raised against an order line or a
// quote line, never both.
export type RequisitionItemPrefillRef = {
  orderItemId?: number;
  quoteItemId?: number;
};

export const apiGetRequisitionItemPrefill = (
  token: string,
  ref: RequisitionItemPrefillRef
): Promise<RequisitionItemPrefillType> => {
  const params = new URLSearchParams(
    ref.orderItemId !== undefined
      ? { orderItemId: String(ref.orderItemId) }
      : { quoteItemId: String(ref.quoteItemId) }
  );
  return fetch(`${ENDPOINT}/item-prefill?${params.toString()}`, {
    method: "GET",
    headers: jsonHeaders(token),
  }).then(handleJsonResponse);
};
